/**
 * Where the product picture goes so its print zone lands on the designer's
 * zone — the whole of "designing on the shirt".
 *
 * The host describes the zone on its picture in FRACTIONS of the picture, so
 * the answer does not depend on the picture's pixel size: the picture is
 * scaled until that rectangle is exactly the on-screen zone, then moved so the
 * two coincide. Returns undefined for a description no picture could satisfy
 * (an empty or out-of-range rectangle), and the designer then falls back to
 * its plain backdrop rather than drawing a shirt in the wrong place.
 */
export interface Rect { left: number; top: number; width: number; height: number }

export function productBackdropRect(
  zone: Rect,
  fraction: { x: number; y: number; w: number; h: number },
): Rect | undefined {
  const { x, y, w, h } = fraction;
  if (![x, y, w, h].every(Number.isFinite)) return undefined;
  if (w <= 0.001 || h <= 0.001 || w > 1 + 1e-6 || h > 1 + 1e-6) return undefined;
  if (x < -1e-6 || y < -1e-6 || x + w > 1 + 1e-6 || y + h > 1 + 1e-6) return undefined;
  const width = zone.width / w;
  const height = zone.height / h;
  return {
    left: zone.left - x * width,
    top: zone.top - y * height,
    width,
    height,
  };
}
